import AddIcon from '@mui/icons-material/Add';
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteBrand, listBrands } from '../../api/business/endpoints';
import type { Brand } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import { CompanySelector } from '../../components/CompanySelector';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useSelectedCompany } from '../../components/useSelectedCompany';
import { sortRows, useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { BrandDialog } from './BrandDialog';

type BrandSortKey = 'name' | 'active' | 'products';

const VALUE_OF: Record<BrandSortKey, (brand: Brand) => string | number | null> = {
  name: (brand) => brand.name,
  active: (brand) => (brand.active ? 1 : 0),
  products: (brand) => brand.productCount,
};

type Dialog = { kind: 'brand'; brand: Brand | null } | { kind: 'delete'; brand: Brand };

/**
 * The company's brands and manufacturers (R0030 WP-06), each with its logo and how many products carry it. The
 * whole list is held, so it sorts in the browser (Conventions/08 §3).
 */
export const BrandsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const selection = useSelectedCompany('products.read');
  const company = selection.company;
  const [term, setTerm] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const sort = useSort<BrandSortKey>('name');
  const canManage =
    company !== null && permissions.writable && permissions.can('products.manage', company.id);

  const query = useQuery({
    queryKey: ['brands', company?.id],
    enabled: company !== null,
    queryFn: () => listBrands(company?.id ?? ''),
  });
  const remove = useAppMutation(
    (brandId: string) => deleteBrand(company?.id ?? '', brandId),
    ['brands', 'product', 'products'],
    () => setDialog(null),
  );

  if (!selection.isLoading && company === null) {
    return (
      <>
        <PageHeader title={t('brands.title')} />
        <ErrorNotice error={selection.error} onRetry={selection.retry} />
        <Paper variant="outlined">
          <EmptyState
            title={t('products.noCompany')}
            detail={t('products.noCompanyDetail')}
            action={<Button onClick={() => navigate('/companies')}>{t('nav.companies')}</Button>}
          />
        </Paper>
      </>
    );
  }

  const needle = term.trim().toLocaleLowerCase('sk');
  const rows = sortRows(
    (query.data ?? []).filter(
      (brand) =>
        (!onlyActive || brand.active) &&
        (needle === '' || brand.name.toLocaleLowerCase('sk').includes(needle)),
    ),
    sort.direction,
    VALUE_OF[sort.key],
  );
  // The editor follows the list: a logo uploaded there shows here at once.
  const editing =
    dialog?.kind === 'brand' && dialog.brand !== null
      ? ((query.data ?? []).find((brand) => brand.id === dialog.brand?.id) ?? dialog.brand)
      : null;

  return (
    <>
      <PageHeader
        title={t('brands.title')}
        subtitle={t('brands.subtitle')}
        action={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CompanySelector
              companies={selection.companies}
              value={company}
              onChange={(companyId) => selection.select(companyId)}
            />
            {canManage ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialog({ kind: 'brand', brand: null })}
              >
                {t('brands.create')}
              </Button>
            ) : null}
          </Stack>
        }
      />
      <ErrorNotice error={query.error ?? selection.error} onRetry={() => void query.refetch()} />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            placeholder={t('brands.searchHint')}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={onlyActive}
                onChange={(event) => setOnlyActive(event.target.checked)}
              />
            }
            label={t('brands.onlyActive')}
          />
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="name">
                  {t('brands.name')}
                </SortableCell>
                <SortableCell sort={sort} column="active">
                  {t('brands.active')}
                </SortableCell>
                <SortableCell sort={sort} column="products" align="right">
                  {t('brands.products')}
                </SortableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading || selection.isLoading ? (
                <TableSkeletonRows columns={4} />
              ) : (
                rows.map((brand) => (
                  <TableRow key={brand.id} hover>
                    <TableCell sx={{ fontWeight: 560 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar
                          variant="rounded"
                          src={brand.logo?.urls.thumb.webp}
                          alt={brand.logo ? brand.name : t('brands.noLogo')}
                          sx={{ width: 36, height: 36, bgcolor: 'action.hover', color: 'text.secondary' }}
                        >
                          {brand.name.slice(0, 1)}
                        </Avatar>
                        {brand.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {brand.active ? (
                        t('common.yes')
                      ) : (
                        <Chip size="small" variant="outlined" label={t('brands.inactive')} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        disabled={brand.productCount === 0}
                        onClick={() => navigate(`/products?brandId=${brand.id}`)}
                      >
                        {brand.productCount}
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      {canManage ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Button size="small" onClick={() => setDialog({ kind: 'brand', brand })}>
                            {t('common.edit')}
                          </Button>
                          <Button
                            size="small"
                            color="warning"
                            onClick={() => setDialog({ kind: 'delete', brand })}
                          >
                            {t('common.delete')}
                          </Button>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && company !== null && rows.length === 0 ? (
          <EmptyState
            title={t('brands.empty')}
            detail={canManage ? t('brands.emptyManage') : t('brands.emptyRead')}
            action={
              canManage ? (
                <Button startIcon={<AddIcon />} onClick={() => setDialog({ kind: 'brand', brand: null })}>
                  {t('brands.create')}
                </Button>
              ) : undefined
            }
          />
        ) : null}
      </Paper>

      {dialog?.kind === 'brand' && company ? (
        <BrandDialog companyId={company.id} brand={editing} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'delete' ? (
        <ConfirmDialog
          title={t('brands.deleteTitle', { name: dialog.brand.name })}
          message={t('brands.deleteMessage', { count: dialog.brand.productCount })}
          confirmLabel={t('common.delete')}
          onConfirm={() => remove.mutate(dialog.brand.id)}
          onClose={() => setDialog(null)}
          isBusy={remove.isPending}
          error={remove.error}
        />
      ) : null}
    </>
  );
};
